import { createSelector } from '@reduxjs/toolkit';
import { selectCultureDetailsCultureId } from 'app/containers/Cultures/slice/selectors';

import { RootState } from 'types';
import { initialState } from '.';
import { selectCultureDetailsCulturesArr } from '../../Cultures/slice/selectors';
import {
  IFieldParameters,
  IMappedField,
  IFieldsByCompanies,
  MapModeEnum,
  IMappedTempField,
  FieldsBoundaries,
  IMapCulture,
} from './types';

import { IFieldProgressWithProgressArea } from 'app/containers/Task/slice/types';
import { getAreaWithUnits } from 'utils/helpers';
import { FIELDS_LAYER_ID } from './map-constants';
import { ICultureVariation } from 'app/containers/Cultures/slice/types';
import {
  filterFieldsByUsedSeedVariations,
  getCultureFieldIds,
  getVariationFieldIds,
} from './helpers';

const selectSlice = (state: RootState) => state.map || initialState;

export const selectMap = createSelector([selectSlice], state => state);

export const selectShowCurrentUserLocation = createSelector(
  [selectMap],
  state => state.showCurrentUserLocation,
);

export const selectVisibleRoutesBySelectedTaskFieldSeasonId = createSelector(
  [selectMap],
  state => state.visibleRoutesBySelectedTaskFieldSeasonId,
);

export const selectHighlightedRoutesGpsVehicleId = createSelector(
  [selectMap],
  state => state.transport.highlightedRoutesGpsVehicleId,
);

export const selectHiddenOnMapGpsVehicleIds = createSelector(
  [selectMap],
  state => state.transport.hiddenOnMapGpsVehicleIds,
);

export const selectIsMapReady = createSelector(
  [selectMap],
  state => state.isMapReady && !state.loading,
);

export const selectPolygons = createSelector(
  [selectMap],
  state => state.polygons,
);

export const selectPolygonsFieldSeasonIds = createSelector(
  [selectPolygons],
  polygons => polygons.map(polygon => polygon.fieldParameters.fieldId),
);

export const selectFieldsOpacity = createSelector([selectMap], state => {
  const fieldsLayer = state.layers.find(layer => layer.id === FIELDS_LAYER_ID);
  if (fieldsLayer) {
    return fieldsLayer.opacity;
  }
  return 1;
});

export const selectActivePolygonId = createSelector(
  [selectMap],
  state => state.activePolygonId,
);

export const selectCurrentCulturePolygons = createSelector(
  [selectMap, selectCultureDetailsCultureId],
  (state, cultureId) => {
    return state.polygons.filter(
      polygon => cultureId === polygon.fieldParameters.cultureId,
    );
  },
);

export const selectCurrentCulturePolygonsIds = createSelector(
  [selectCurrentCulturePolygons],
  polygons => {
    return polygons.map(polygon => polygon.fieldParameters.fieldId);
  },
);

export const selectSelectedPolygonIds = createSelector(
  [selectMap],
  state => state.selectedPolygons,
);

export const selectSelectedFields = createSelector([selectMap], state => {
  return state.selectedPolygons.reduce(
    (accumulator: IFieldParameters[], id) => {
      const field = state.polygons.find(
        polygon => polygon.fieldParameters.fieldId === id,
      );
      if (field) {
        accumulator.push(field.fieldParameters);
      }

      return accumulator;
    },
    [],
  );
});

export const selectSelectedFieldsTotalArea = createSelector(
  [selectSelectedFields],
  fields => {
    return fields.reduce((accumulator, field) => {
      return (accumulator += field.areaNum);
    }, 0);
  },
);

export const selectSelectedFieldsTotalAreaWithUnits = createSelector(
  [selectSelectedFieldsTotalArea],
  areaNum => {
    return getAreaWithUnits(areaNum);
  },
);

// Use only with memoized id
export const makeSelectSelectedCompanyFields = (companyId?: number) =>
  createSelector([selectSelectedFields], (fields: IFieldParameters[]) => {
    return fields.filter(field => !companyId || field.companyId === companyId);
  });

export const selectSelectedFieldsByCompany = createSelector(
  [selectSelectedFields],
  selectedFields => {
    const fieldsByCompanies: IFieldsByCompanies[] = [];
    selectedFields.forEach(field => {
      const newField: IFieldsByCompanies = {
        companyId: field.companyId,
        companyName: field.companyName,
        fieldSeasonIds: [field.fieldId],
        fieldParams: [field],
        areaNum: field.areaNum,
        area: getAreaWithUnits(field.areaNum),
      };

      const existingCompany = fieldsByCompanies.find(
        sortedField => sortedField.companyId === field.companyId,
      );
      if (existingCompany) {
        existingCompany.fieldSeasonIds.push(field.fieldId);
        existingCompany.fieldParams.push(field);
        existingCompany.areaNum += field.areaNum;
        existingCompany.area = getAreaWithUnits(existingCompany.areaNum);
      } else {
        fieldsByCompanies.push(newField);
      }
    });
    return fieldsByCompanies;
  },
);

export const selectMapLoading = createSelector(
  [selectMap],
  state => state.loading,
);

export const selectMapMode = createSelector(
  [selectMap],
  state => state.mapMode,
);

export const selectLayers = createSelector([selectMap], state => state.layers);

export const selectVisibleWmsTileLayers = createSelector(
  [selectLayers],
  layers => {
    return layers
      .map((layer, index) => ({ ...layer, index }))
      .filter(layer => layer.layerType === 'wmsTile' && layer.visible)
      .sort((a, b) => a.id.localeCompare(b.id));
  },
);

export const selectPolygonLayers = createSelector([selectLayers], layers =>
  layers.filter(layer => layer.layerType === 'polygon'),
);

export const selectCultureMenuSelectedIds = createSelector(
  [selectMap],
  state => state.cultureMenuSelectedIds,
);

export const selectCultureMenuSelectedVariationsIds = createSelector(
  [selectMap],
  state => state.cultureMenuSelectedVariationsIds,
);

export const selectFitCoordArrOnMap = createSelector(
  [selectMap],
  state => state.fitCoordArrOnMap,
);

export const selectInitialFieldsProgress = createSelector(
  [selectMap],
  state => state.fieldsProgress.fieldsProgressArr,
);

export const selectFieldsProgress = createSelector(
  [selectMap],
  state => state.fieldsProgress.updatedFieldsArr,
);

export const selectFieldsProgressAreas = createSelector([selectMap], state => {
  let totalProgressArea = 0;
  const fields: IFieldProgressWithProgressArea[] = [];

  state.fieldsProgress.updatedFieldsArr?.forEach(field => {
    const oldField = state.fieldsProgress.fieldsProgressArr?.find(
      f => f.fieldId === field.fieldId,
    );
    if (oldField) {
      if (oldField.progress !== field.progress) {
        const progressArea =
          ((field.progress - oldField.progress) / 100) * field.area;
        if (progressArea > 0) {
          totalProgressArea += progressArea;
          fields.push({ ...field, progressArea });
        }
      }
    }
  });
  return { fields, totalProgressArea };
});

export const selectMappedFields = createSelector(
  [selectMap],

  ({ fieldsProgress, polygons, selectedPolygons: selectedPolygonsIds }) => {
    const fields: IMappedField[] = polygons.map(polygon => {
      const isSelected = Boolean(
        selectedPolygonsIds.find(id => id === polygon.fieldParameters.fieldId),
      );

      const mappedField: IMappedField = { ...polygon, isSelected };

      const fieldProgressUpdateObj = fieldsProgress.updatedFieldsArr?.find(
        field => field.fieldId === polygon.fieldParameters.fieldId,
      );

      if (fieldProgressUpdateObj) {
        mappedField.fieldProgressUpdate = fieldProgressUpdateObj.progress;
      }

      return mappedField;
    });

    return fields;
  },
);

export const selectMappedCultureFields = createSelector(
  [selectMappedFields, selectCultureDetailsCultureId],
  (mappedFields, cultureId) => {
    if (!cultureId) {
      return [];
    }

    return mappedFields.filter(
      field => field.fieldParameters.cultureId === cultureId,
    );
  },
);
export const selectCultureAndNotCultureFields = createSelector(
  [selectMappedFields, selectCultureDetailsCultureId],
  (mappedFields, cultureId) => {
    if (!cultureId) {
      return { cultureFields: [], notCultureFields: mappedFields };
    }

    const cultureFields = mappedFields.filter(
      field => field.fieldParameters.cultureId === cultureId,
    );
    const notCultureFields = mappedFields.filter(
      field => !(field.fieldParameters.cultureId === cultureId),
    );

    return { cultureFields, notCultureFields };
  },
);

export const selectOnlySelectedMappedFields = createSelector(
  [selectMappedFields],
  mappedFields => {
    return mappedFields.filter(field => field.isSelected);
  },
);

export const selectNotSelectedMappedFields = createSelector(
  [selectMappedFields],
  mappedFields => {
    return mappedFields.filter(field => !field.isSelected);
  },
);

export const selectSingleFieldOnMap = createSelector(
  [selectMap, selectMappedFields],
  (state, mappedFields) => {
    const field = mappedFields.find(
      field => field.fieldParameters.fieldId === state.singleFieldOnMap.fieldId,
    );
    if (field) {
      return field;
    }
    return null;
  },
);

export const selectOnlySelectedMappedFieldsFilteredByCulture = createSelector(
  [
    selectCultureMenuSelectedIds,
    selectCultureMenuSelectedVariationsIds,
    selectOnlySelectedMappedFields,
  ],
  (selectedCultureIds, selectedVariationsIds, mappedFields) => {
    const filteredByCulture = mappedFields.filter(field =>
      selectedCultureIds.includes(field.fieldParameters.cultureId || 0),
    );

    const filteredByCultureVariation = filterFieldsByUsedSeedVariations(
      filteredByCulture,
      selectedVariationsIds,
    );
    return filteredByCultureVariation;
  },
);

export const selectMappedFieldsFilteredByCulture = createSelector(
  [
    selectCultureMenuSelectedIds,
    selectCultureMenuSelectedVariationsIds,
    selectMappedFields,
  ],
  (selectedCultureIds, selectedVariationsIds, mappedFields) => {
    const filteredByCulture = mappedFields.filter(field =>
      selectedCultureIds.includes(field.fieldParameters.cultureId || 0),
    );
    const filteredByCultureVariation = filterFieldsByUsedSeedVariations(
      filteredByCulture,
      selectedVariationsIds,
    );
    return filteredByCultureVariation;
  },
);
export const selectMappedFieldsFilteredByCultureAndLayer = createSelector(
  [selectPolygonLayers, selectMappedFieldsFilteredByCulture],
  (layers, mappedFields) => {
    const visiblePolygonLayers = layers.filter(layer => layer.visible);

    return mappedFields.filter(field =>
      visiblePolygonLayers.find(layer => layer.id === field.layerId),
    );
  },
);

export const selectCoordinatesArr = createSelector(
  [
    selectMap,
    selectCurrentCulturePolygons,
    selectMappedFieldsFilteredByCulture,
  ],
  (state, culturePolygons, fieldsFilteredByCulture) => {
    switch (state.mapMode) {
      case MapModeEnum.upload:
        return state.uploadFields.tempPolygons.map(
          polygon => polygon.coordinates,
        );

      case MapModeEnum.singleField:
        const singlePolygon = state.polygons.find(
          polygon =>
            polygon.fieldParameters.fieldId === state.singleFieldOnMap.fieldId,
        );
        const arr: FieldsBoundaries[] = [];
        if (singlePolygon) {
          arr.push(singlePolygon.coordinates);
        }
        return arr;

      case MapModeEnum.taskFields:
      case MapModeEnum.progress:
        return state.polygons
          .filter(polygon =>
            state.selectedPolygons.includes(polygon.fieldParameters.fieldId),
          )
          .map(polygon => polygon.coordinates);

      case MapModeEnum.culture:
        return culturePolygons.map(polygon => polygon.coordinates);

      default:
        return fieldsFilteredByCulture.map(polygon => polygon.coordinates);
    }
  },
);

export const selectSingleFieldOnMapFieldId = createSelector(
  [selectMap],
  state => {
    return state.singleFieldOnMap.fieldId;
  },
);

export const selectTempPolygons = createSelector(
  [selectMap],
  state => state.uploadFields.tempPolygons,
);

export const selectOnlyMarkedTempPolygons = createSelector(
  [selectMap],
  ({ uploadFields }) =>
    uploadFields.tempPolygons.filter(polygon =>
      Boolean(
        uploadFields.selectedIds.find(selectedId => selectedId === polygon.id),
      ),
    ),
);

export const selectSelectedTempPolygonsIds = createSelector(
  [selectMap],
  state => state.uploadFields.selectedIds,
);

export const selectMappedTempFields = createSelector(
  [selectMap],
  ({ uploadFields: { tempPolygons, selectedIds } }) => {
    const fields = tempPolygons.map(polygon => {
      const isSelected = Boolean(selectedIds.find(id => id === polygon.id));

      const mappedTempField: IMappedTempField = { ...polygon, isSelected };
      return mappedTempField;
    });
    return fields;
  },
);

export const selectTempPolygonById = (state: RootState, id: string) => {
  return state.map?.uploadFields.tempPolygons.find(
    tempPolygon => tempPolygon.id === id,
  );
};

export const selectPolygonByFieldId = (state: RootState, fieldId: number) => {
  return state.map?.polygons.find(
    polygon => polygon.fieldParameters.fieldId === fieldId,
  );
};

export const selectCultureFieldsGroupedByCompanies = createSelector(
  [selectMappedCultureFields, selectCultureDetailsCulturesArr],
  (mappedFields, culturesArr) => {
    const result = culturesArr.map(cultureData => {
      const companyId = cultureData.company.id;
      const fields = mappedFields.filter(
        field => field.fieldParameters.companyId === companyId,
      );
      return {
        ...cultureData,
        mappedFields: fields,
      };
    });
    return result;
  },
);

export const selectDeletedCulturePolygonsIds = createSelector(
  [selectCurrentCulturePolygonsIds, selectSelectedPolygonIds],
  (cultureFieldsIds, selectedIds) => {
    const deletedIds = cultureFieldsIds.filter(
      cultureId => !selectedIds.find(selectedId => selectedId === cultureId),
    );
    return deletedIds;
  },
);

export const selectMapCultures = createSelector(
  [
    selectPolygons,
    selectCultureMenuSelectedIds,
    selectCultureMenuSelectedVariationsIds,
  ],
  (polygons, selectedCulturesIds, selectedVariationsIds) => {
    const fieldsCultureVariations = polygons
      .filter(polygon => polygon.fieldParameters.cultureVariationsArr.length)
      .reduce(
        (
          accumulator: {
            cultureVariation: ICultureVariation;
            areaNum: number;
            area: string;
            isSelected: boolean;
          }[],
          polygon,
        ) => {
          const variations = polygon.fieldParameters.cultureVariationsArr.map(
            variation => ({
              cultureVariation: variation,
              areaNum: polygon.fieldParameters.areaNum,
              area: '',
            }),
          );
          // merge same variations
          variations.forEach(variation => {
            const variationIndex = accumulator.findIndex(
              accumulatorVariation =>
                accumulatorVariation.cultureVariation.id ===
                variation.cultureVariation.id,
            );
            if (variationIndex === -1) {
              const isVariationSelected = selectedVariationsIds.includes(
                variation.cultureVariation.id,
              );

              accumulator.push({
                ...variation,
                isSelected: isVariationSelected,
              });
            } else {
              accumulator[variationIndex].areaNum += variation.areaNum;
            }
          });
          return accumulator;
        },
        [],
      )
      .map(variation => ({
        ...variation,
        area: getAreaWithUnits(variation.areaNum),
      }));

    const mapCultures = polygons.reduce(
      (
        accumulator: IMapCulture[],
        { fieldParameters: { cultureColor, cultureName, cultureId, areaNum } },
      ) => {
        // If field doesn't have culture - assign 0 as cultureId;
        const cultId = cultureId || 0;

        const indexOfCulture = accumulator.findIndex(
          culture => culture.cultureId === cultId,
        );

        if (indexOfCulture === -1) {
          const cultureVariations = fieldsCultureVariations.filter(
            cultureVariationItem =>
              cultureVariationItem.cultureVariation.culture.id === cultId,
          );

          accumulator.push({
            cultureName: cultureName,
            cultureId: cultId,
            cultureColor: cultureColor,
            isSelected: selectedCulturesIds.includes(cultId),
            areaNum: areaNum,
            area: '',
            cultureVariations,
          });
        } else {
          accumulator[indexOfCulture].areaNum += areaNum;
        }
        return accumulator;
      },
      [],
    );

    return mapCultures.map(culture => ({
      ...culture,
      area: getAreaWithUnits(culture.areaNum),
    }));
  },
);

export const selectMapCulturesWithCheckboxValues = createSelector(
  [selectMapCultures, selectSelectedPolygonIds, selectPolygons],
  (mapCultures, selectedIds, polygons) => {
    const result = mapCultures.map(culture => {
      const cultureFieldIds = getCultureFieldIds(
        culture.cultureId || null,
        polygons,
      );

      const checked = cultureFieldIds.every(v => selectedIds.includes(v));
      const indeterminate = cultureFieldIds.some(r => selectedIds.includes(r));

      const cultureVariations = culture.cultureVariations.map(variation => {
        const variationFieldIds = getVariationFieldIds(
          variation.cultureVariation.id,
          polygons,
        );

        const checked = variationFieldIds.every(v => selectedIds.includes(v));
        const indeterminate = variationFieldIds.some(r =>
          selectedIds.includes(r),
        );

        return {
          ...variation,
          checked,
          indeterminate: checked ? false : indeterminate,
        };
      });

      return {
        ...culture,
        cultureVariations,
        checked,
        indeterminate: checked ? false : indeterminate,
      };
    });

    return result;
  },
);

export const selectDrawnPolygonById = (state: RootState, id: string) => {
  return state.field?.drawnFields.polygonsArr.find(
    polygon => polygon.id === id,
  );
};
